import { VisuallyHidden } from '@reach/visually-hidden';
import React, { ChangeEventHandler, forwardRef, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import styled from 'styled-components';

import BaseCard from '@ui/atoms/BaseCard';
import BasicText from '@ui/atoms/BasicText';
import { CardRow } from '@ui/atoms/CardRow';
import FlexColumn from '@ui/atoms/FlexColumn';
import Pill from '@ui/atoms/Pill';

import padded from '@ui/styled/Padded';

import { doesNotExist } from '@utils/general';
import { centsToDollars } from '@utils/money';

import { IFolxMembershipOption } from '@folx-types/shared-folx-membership-information';
import { ColorTypes } from '@folx-types/theme';

import theme from '@theme/FolxTheme';

interface IMembershipOptionCardProps {
	content: IFolxMembershipOption;
	price?: number;
	onClick?: () => void;
	isSelected?: boolean;
	badgeColor?: ColorTypes;
}

interface IMembershipOptionCardWithInputProps extends IMembershipOptionCardProps {
	optionValue: string;
	value: string;
	onChange?: ControllerRenderProps['onChange'];
	onBlur?: ChangeEventHandler<HTMLInputElement>;
	error?: string;
	name?: string;
}

interface IMembershipOptionDisplayProps {
	content: IFolxMembershipOption;
	stripePrice?: number;
	badgeColor?: ColorTypes;
}

const Price = styled(BasicText)`
	font-weight: bold;
	font-size: ${theme.fontSizes.body.smmd};
`;

const PaddedBaseCard = styled(padded(BaseCard))`
	cursor: pointer;
`;
const PaddedBasicText = padded(BasicText);

export const MembershipOptionDisplay = ({
	content,
	stripePrice,
	badgeColor = 'yellow',
}: IMembershipOptionDisplayProps) => {
	const { title, price, priceSubtext, description, badge, disclaimer } = content;

	return (
		<FlexColumn gap="xs">
			<CardRow>
				<BasicText>{title}</BasicText>
				{badge && <Pill color={badgeColor} title={badge} />}
			</CardRow>
			<FlexColumn gap="xs">
				<Price variant="body">
					{doesNotExist(stripePrice)
						? price
						: `${centsToDollars(stripePrice ?? 0)} / ${price.split('/')[1]}`}
				</Price>
				{priceSubtext ? <BasicText variant="smallText">{priceSubtext}</BasicText> : null}
			</FlexColumn>
			<BasicText variant="smallText">{description}</BasicText>
			{disclaimer && (
				<PaddedBasicText variant="smallText" paddingTop="xs">
					{disclaimer}
				</PaddedBasicText>
			)}
		</FlexColumn>
	);
};

export const MembershipOptionCard = ({
	content,
	price,
	onClick,
	isSelected,
	badgeColor = 'yellow',
}: IMembershipOptionCardProps) => {
	const isClickable = !!onClick;

	return (
		<PaddedBaseCard
			bgColor="white"
			borderColor={isSelected ? 'black' : 'white'}
			radius="md"
			shadow
			spacing="sm"
			padding="md"
			paddingTop="sm"
			paddingBottom="sm"
			onClick={onClick}>
			<MembershipOptionDisplay content={content} badgeColor={badgeColor} stripePrice={price} />
		</PaddedBaseCard>
	);
};

type Ref = HTMLInputElement;

export const MembershipOptionCardWithInput = forwardRef<Ref, IMembershipOptionCardWithInputProps>(
	(
		{
			content,
			price,
			onClick,
			isSelected,
			badgeColor = 'yellow',
			name,
			value,
			onChange = () => {},
			onBlur = () => {},
			optionValue,
		},
		ref
	) => {
		const [focused, setFocused] = useState(false);

		return (
			<>
				<VisuallyHidden>
					<input
						aria-label={content.title}
						type="radio"
						id={optionValue}
						name={name}
						checked={value === optionValue}
						value={optionValue}
						onFocus={() => setFocused(true)}
						onBlur={(e) => {
							setFocused(false);
							onBlur(e);
						}}
						onChange={onChange}
						ref={ref}
					/>
				</VisuallyHidden>
				<PaddedBaseCard
					bgColor="white"
					borderless={!(value === optionValue)}
					borderColor={value === optionValue ? 'black' : 'white'}
					radius="md"
					shadow
					spacing="sm"
					padding="md"
					paddingTop="sm"
					paddingBottom="sm"
					onClick={() => onChange(optionValue)}>
					<MembershipOptionDisplay content={content} badgeColor={badgeColor} stripePrice={price} />
				</PaddedBaseCard>
			</>
		);
	}
);
